import Alpine from "alpinejs";
import Fuse from "fuse.js";

Alpine.data("resourceCentre", (currentCompanyId) => ({
  query: "",
  loading: false,
  grouped: true, // Grouped is used to control accordion behavior
  _resources: [],
  currentCompanyId: currentCompanyId,
  get accordionItems() {
    return this.$root.querySelectorAll("details");
  },
  get resources() {
    return Object.keys(this._resources).reduce((accumulator, key) => {
      let resources = this._resources[key].all;

      // Apply search filter if query is not empty
      if (this.query.trim() !== "") {
        const fuseResults = this._resources[key].fuse.search(this.query);
        resources = fuseResults.map((result) => result.item);

        // When searching, expand all accordion items and disable grouping
        this.$nextTick(() => {
          this.grouped = false;
          this.accordionItems.forEach((item) => {
            item.open = true;
          });
        });
      } else {
        // When not searching, collapse all accordion items except the first and enable grouping
        this.$nextTick(() => {
          this.grouped = true;
        });
      }

      if (resources.length > 0) {
        accumulator[key] = resources;
      }

      return accumulator;
    }, {});
  },
  parseArrayValue(value) {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  },
  init() {
    this.loading = true;
    this.getAllResources().then(() => {
      // map API resources into the structure consumed by the template
      this._resources = this._resources.map((resource) => {
        return {
          file: resource.file || null,
          title: resource.title || "",
          type: this.parseArrayValue(resource.type),
          customers: this.parseArrayValue(resource.customer_visibility),
        };
      });

      this._resources = this._resources.filter((resource) => {
        if (this.currentCompanyId) {
          // If there's a company, only show resources that are either public (i.e. with no customer visibility) or assigned to the current company
          return (
            resource.customers.length === 0 ||
            resource.customers.includes(
              `gid://shopify/Company/${this.currentCompanyId}`,
            )
          );
        }

        // Without a company, only show public resources.
        return resource.customers.length === 0;
      });

      // group resources by "type"
      this._resources = this._resources.reduce((accumulator, resource) => {
        let types = resource.type || [];
        types.forEach((type) => {
          if (!accumulator[type]) {
            accumulator[type] = [];
          }
          accumulator[type].push(resource);
        });
        return accumulator;
      }, {});

      // setup fuse
      Object.keys(this._resources).forEach((key) => {
        this._resources[key] = {
          all: this._resources[key],
          fuse: new Fuse(this._resources[key], {
            keys: ["title", "type"],
            threshold: 0.3,
          }),
        };
      });

      // check for query param
      const urlParams = new URLSearchParams(window.location.search);
      const queryParam = urlParams.get("q");
      if (queryParam) {
        this.query = queryParam;
      }

      this.loading = false;
    });
  },
  async getAllResources() {
    try {
      const apiUrl = new URL("https://shopify-api.clearwebserver.co.uk/rozone/");

      if (this.currentCompanyId) {
        apiUrl.searchParams.set("company", String(this.currentCompanyId));
      }

      const response = await fetch(apiUrl.toString());

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      this._resources = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching resources:", error);
      this._resources = [];
    }
  },
}));
